import { useEffect, useRef, useState } from "react";

interface Run {
  date: string;
  score: number;
}

interface Props {
  runs: Run[];
  color: string;
  run: boolean;
}

export function ScoreChart({ runs, color, run }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(640);
  const h = 240;
  const padL = 8;
  const padR = 8;
  const padT = 18;
  const padB = 34;

  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(([e]) => setW(e.contentRect.width));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  if (runs.length < 2) {
    return <div className="scorechart" ref={ref} />;
  }

  const innerW = w - padL - padR;
  const innerH = h - padT - padB;
  const edge = 26;
  const xs = (i: number) => padL + edge + (i / (runs.length - 1)) * (innerW - edge * 2);
  const ys = (s: number) => padT + innerH - ((s - 0) / 100) * innerH;

  const pts: [number, number][] = runs.map((r, i) => [xs(i), ys(r.score)]);
  const line = pts.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ");
  const lastX = pts[pts.length - 1][0].toFixed(1);
  const firstX = pts[0][0].toFixed(1);
  const baseY = (padT + innerH).toFixed(1);
  const area = `${line} L${lastX} ${baseY} L${firstX} ${baseY} Z`;

  const grid = [25, 50, 75];

  return (
    <div className="scorechart" ref={ref}>
      <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
        {grid.map((g) => (
          <g key={g}>
            <line
              x1={padL}
              x2={w - padR}
              y1={ys(g)}
              y2={ys(g)}
              stroke="var(--ink)"
              strokeOpacity="0.12"
              strokeWidth="1.5"
              strokeDasharray="4 6"
            />
            <text x={padL + 2} y={ys(g) - 5} className="scorechart-glabel">{g}</text>
          </g>
        ))}
        <path d={area} fill={color} fillOpacity="0.16" style={{ transition: "all .5s var(--ease)" }} />
        <path
          d={line}
          fill="none"
          stroke={color}
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            strokeDasharray: 1400,
            strokeDashoffset: run ? 0 : 1400,
            transition: "stroke-dashoffset 1.1s var(--ease)"
          }}
        />
        {pts.map((p, i) => (
          <g key={i} style={{ opacity: run ? 1 : 0, transition: `opacity .3s ${0.6 + i * 0.12}s` }}>
            <circle cx={p[0]} cy={p[1]} r="6" fill="var(--card)" stroke="var(--ink)" strokeWidth="2.5" />
            <circle cx={p[0]} cy={p[1]} r="2.5" fill={color} />
            <text
              x={p[0]}
              y={h - 12}
              textAnchor={i === 0 ? "start" : i === pts.length - 1 ? "end" : "middle"}
              className="scorechart-xlabel"
            >
              {runs[i].date}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}
